import { useState, useEffect, useRef, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { Bell, Check, CheckCheck, FileText, BookOpen, MessageSquare, Users, Trophy, Upload, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";

const typeConfig = {
  announcement: { icon: MessageSquare, color: "text-cyan-600", bg: "bg-cyan-50" },
  assignment: { icon: FileText, color: "text-blue-600", bg: "bg-blue-50" },
  quiz: { icon: BookOpen, color: "text-purple-600", bg: "bg-purple-50" },
  submission: { icon: Upload, color: "text-green-600", bg: "bg-green-50" },
  grade: { icon: Star, color: "text-amber-600", bg: "bg-amber-50" },
  feedback: { icon: MessageSquare, color: "text-indigo-600", bg: "bg-indigo-50" },
  join: { icon: Users, color: "text-teal-600", bg: "bg-teal-50" },
  document: { icon: FileText, color: "text-orange-600", bg: "bg-orange-50" },
  deadline: { icon: Bell, color: "text-red-600", bg: "bg-red-50" },
};

export function NotificationBell({ token }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { headers });
      setUnreadCount(res.data.total || 0);
    } catch {}
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications/recent`, { headers });
      setNotifications(res.data.notifications || []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const markAsRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {}, { headers });
      setNotifications(prev => prev.map(n => n.notification_id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleNotificationClick = (notif) => {
    if (!notif.read) markAsRead(notif.notification_id);
    if (notif.link) window.location.href = notif.link;
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1" data-testid="unread-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-background border border-border rounded-xl shadow-xl z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs text-primary h-7" data-testid="mark-all-read-btn">
                <CheckCheck className="w-3 h-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notifications list */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading && notifications.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div>
            )}

            {!loading && notifications.length === 0 && (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            )}

            {notifications.map((n) => {
              const cfg = typeConfig[n.type] || typeConfig.announcement;
              const Icon = cfg.icon;
              const timeAgo = n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : "";

              return (
                <div
                  key={n.notification_id}
                  className={`flex items-start gap-3 p-3 border-b border-border/30 cursor-pointer transition-colors hover:bg-muted/50 ${
                    !n.read ? "bg-primary/5" : ""
                  }`}
                  onClick={() => handleNotificationClick(n)}
                  data-testid={`notification-${n.notification_id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg}`}>
                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-tight ${!n.read ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                      {n.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-1">{timeAgo}</p>
                  </div>
                  {!n.read && (
                    <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
